import type { GetOrderDto, GetOrderPhotosDto, GetPhotoDto } from "src/services/generated-api";

/**
 * The free 30 × 40 enlargement the basket earns at the gift line (backend:
 * FreeEnlargementService, `freeEnlargementEnabled`). Repricing adds and
 * removes it as an ordinary photo line flagged `isFreeEnlargement`: one photo,
 * 0 €. The customer never edits it like a line of their own — no count, no
 * options, no delete, no uploads into it; only its photo changes, through
 * PUT /order/:id/free-enlargement.
 *
 * `isFreeEnlargement` is typed by hand here until the client is regenerated.
 */
type MaybeFreeLine = Pick<GetOrderPhotosDto, "photos"> & { isFreeEnlargement?: boolean };

export function isFreeEnlargementLine(line: unknown): boolean {
  return (line as { isFreeEnlargement?: boolean } | null | undefined)?.isFreeEnlargement === true;
}

/** The customer's own photo lines: everything but the free enlargement. */
export function paidPhotoLines<T>(lines: readonly T[] | null | undefined): T[] {
  return (lines ?? []).filter((line) => !isFreeEnlargementLine(line));
}

/** The order's free enlargement line, or null when the basket has none. */
export function freeEnlargementLine(
  order: Pick<GetOrderDto, "orderPhotos"> | null | undefined,
): GetOrderPhotosDto | null {
  return (order?.orderPhotos ?? []).find((line) => isFreeEnlargementLine(line as MaybeFreeLine)) ?? null;
}

/** One stored file: the API sends `key` (untyped in the client); else the url, else the id. */
export function photoFileKey(photo: GetPhotoDto): string {
  return (photo as { key?: string }).key || photo.url || String(photo.id);
}

/**
 * The photos the customer can choose for the enlargement: those in their paid
 * lines, one per stored file (the same rule the backend applies).
 */
export function freeEnlargementChoices(
  order: Pick<GetOrderDto, "orderPhotos"> | null | undefined,
): GetPhotoDto[] {
  const seen = new Set<string>();
  const out: GetPhotoDto[] = [];
  for (const line of paidPhotoLines(order?.orderPhotos)) {
    for (const photo of line.photos ?? []) {
      if (!photo || (photo.count ?? 0) <= 0) continue;
      const file = photoFileKey(photo);
      if (seen.has(file)) continue;
      seen.add(file);
      out.push(photo);
    }
  }
  return out;
}
